import { useBlocker } from "@tanstack/react-router"
import { useCallback, useEffect, useState } from "react"
import { ConfirmDialog } from "@holigral/shared/ui/composed"

type Props = {
  autoSave: boolean
  save: () => Promise<unknown>
  isDirty: boolean
  isValid: boolean
  confirmMessage?: string
}

export default function PageLeave({
  autoSave,
  save,
  isDirty,
  isValid,
  confirmMessage = "Unable to save your changes. Your changes will be lost if you continue without saving.",
}: Props) {
  const [isSubmitting, setSubmitting] = useState(false)
  const [isSubmittedSuccessfully, setSubmittedSuccessfully] = useState(false)

  const { status, proceed, reset } = useBlocker({
    disabled: !isDirty,
    shouldBlockFn: async () => {
      if (!isValid) {
        return true
      }
      if (!autoSave) {
        return true
      }

      try {
        setSubmittedSuccessfully(false)
        setSubmitting(true)

        await save()

        setSubmittedSuccessfully(true)
      } catch (e) {
        console.error(e)
      }

      setSubmitting(false)

      return true
    },
    withResolver: true,
  })

  const isBlocked = status === "blocked"

  const handleClose = useCallback(
    (confirmed: boolean) => {
      if (!isBlocked) {
        return
      }

      if (confirmed) {
        proceed()
      } else {
        reset()
      }
    },
    [isBlocked, proceed, reset],
  )

  useEffect(() => {
    if (isSubmittedSuccessfully) {
      handleClose(true)
    }
  }, [isSubmittedSuccessfully, handleClose])

  return (
    <ConfirmDialog
      open={isBlocked && !isSubmitting}
      description={confirmMessage}
      onClose={handleClose}
    />
  )
}
